import { tingida } from './paleta'
import { corDe, materialDe, type Planta } from './planta'

/**
 * Kit da construção (SPEC-20261011-2023): placas de chão e trechos de parede feitos no Blender,
 * nos dois visuais. O navegador monta a planta livre com eles, como monta os móveis.
 */

export type ConjuntoDoKit = 'blocos' | 'realista'
type Peca = { x: number; y: number; w: number; h: number; arq: string }
type Kit = Record<ConjuntoDoKit, { chao: Record<string, { arq: string; n: number; tinge: boolean }>; parede: Record<string, Peca> }>

const VERSAO = 1
const ESCALA = 3
const base = `/escritorio/construcao`
let pedido: Promise<Kit | null> | null = null
export function kitDaConstrucao() {
  pedido ??= fetch(`${base}/manifesto.json?v=${VERSAO}`)
    .then((r) => (r.ok ? (r.json() as Promise<Kit>) : null))
    .catch(() => null)
  return pedido
}

const imagens = new Map<string, Promise<HTMLImageElement | null>>()
function carregar(src: string) {
  let p = imagens.get(src)
  if (!p) {
    p = new Promise((ok) => {
      const img = new Image()
      img.decoding = 'async'
      img.onload = () => ok(img)
      img.onerror = () => ok(null)
      img.src = src
    })
    imagens.set(src, p)
  }
  return p
}
const src = (c: ConjuntoDoKit, arq: string) => `${base}/${c}/${arq}.webp?v=${VERSAO}`

/** Peça desenhável já pronta (pintada, se tiver cor), com onde ela vai na tela. */
export type PecaPronta = { img: CanvasImageSource; dx: number; dy: number; chave: number }
export type PlantaPronta = {
  /** Placas de chão por código ("carpete:#hex"), para encher com padrão. */
  chaos: Map<string, CanvasImageSource>
  paredes: PecaPronta[]
  n: number
}

/** Carrega e pinta o que a planta usa. ox, oy: origem do tabuleiro em px (sem a escala 3). */
export async function prepararPlanta(c: ConjuntoDoKit, p: Planta, ox: number, oy: number): Promise<PlantaPronta | null> {
  const kit = (await kitDaConstrucao())?.[c]
  if (!kit) return null
  const chaos = new Map<string, CanvasImageSource>()
  const usados = [...new Set(p.chao.filter((x): x is string => !!x))]
  await Promise.all(
    usados.map(async (codigo) => {
      const m = materialDe(codigo)
      const info = m ? kit.chao[m] : undefined
      if (!info) return
      const img = await carregar(src(c, info.arq))
      if (!img) return
      const cor = corDe(codigo)
      chaos.set(codigo, info.tinge && cor ? await tingida(img, null, cor, m === 'carpete' ? 'tapetes' : 'livre') : img)
    }),
  )
  const paredes = await Promise.all(
    p.paredes.map(async ([x, y, o, tipo, cor]) => {
      const peca = kit.parede[`${tipo}|${o === 0 ? 'h' : 'v'}`]
      if (!peca) return null
      const img = await carregar(src(c, peca.arq))
      if (!img) return null
      const pronta = cor ? await tingida(img, src(c, `${peca.arq}-m`), cor, 'livre') : img
      return {
        img: pronta,
        dx: Math.round((ox + x * 16 + peca.x) * ESCALA),
        dy: Math.round((oy + y * 13.5 + peca.y) * ESCALA),
        // de trás para a frente: a deitada vale a linha dela; a em pé, a ponta da frente
        chave: o === 0 ? y : y + 1 - 0.01,
      } satisfies PecaPronta
    }),
  )
  const n = Object.values(kit.chao)[0]?.n ?? 10
  return { chaos, paredes: paredes.filter((x): x is PecaPronta => !!x), n }
}

/** Pinta o chão (em faixas do mesmo material, com a placa repetindo sem emenda) e a borda da laje. */
export function pintarChao(g: CanvasRenderingContext2D, pronta: PlantaPronta, p: Planta, ox: number, oy: number) {
  const padroes = new Map<string, CanvasPattern>()
  for (const [codigo, img] of pronta.chaos) {
    const pad = g.createPattern(img, 'repeat')
    if (!pad) continue
    pad.setTransform(new DOMMatrix().translate(ox * ESCALA, oy * ESCALA))
    padroes.set(codigo, pad)
  }
  const X = (x: number) => (ox + x * 16) * ESCALA
  const Y = (y: number) => (oy + y * 13.5) * ESCALA
  for (let y = 0; y < p.h; y++) {
    let x = 0
    while (x < p.w) {
      const codigo = p.chao[y * p.w + x] ?? null
      let x1 = x + 1
      while (x1 < p.w && (p.chao[y * p.w + x1] ?? null) === codigo) x1++
      const pad = codigo ? padroes.get(codigo) : undefined
      if (pad) {
        g.fillStyle = pad
        // 1 px a mais embaixo: a linha seguinte cobre, e não sobra fresta entre as linhas
        g.fillRect(Math.floor(X(x)), Math.floor(Y(y)), Math.ceil(X(x1)) - Math.floor(X(x)), Math.ceil(Y(y + 1)) - Math.floor(Y(y)) + 1)
      }
      x = x1
    }
  }
  // a laje: frente do piso onde o chão acaba
  for (let y = 0; y < p.h; y++)
    for (let x = 0; x < p.w; x++) {
      if (!p.chao[y * p.w + x] || (y + 1 < p.h && p.chao[(y + 1) * p.w + x])) continue
      const y0 = Math.ceil(Y(y + 1))
      const gr = g.createLinearGradient(0, y0, 0, y0 + 27)
      gr.addColorStop(0, '#9aa3b5')
      gr.addColorStop(1, '#6a7286')
      g.fillStyle = gr
      g.fillRect(Math.floor(X(x)), y0, Math.ceil(X(x + 1)) - Math.floor(X(x)), 27)
      g.fillStyle = '#4a4f63'
      g.fillRect(Math.floor(X(x)), y0, Math.ceil(X(x + 1)) - Math.floor(X(x)), 3)
    }
  // sombrinha no chão rente às paredes (o kit vem sem chão em volta)
  g.save()
  g.globalCompositeOperation = 'multiply'
  for (const [x, y, o] of p.paredes) {
    if (o === 0) {
      const gr = g.createLinearGradient(0, Y(y), 0, Y(y) + 22)
      gr.addColorStop(0, 'rgba(60,45,30,.28)')
      gr.addColorStop(1, 'rgba(60,45,30,0)')
      g.fillStyle = gr
      g.fillRect(X(x), Y(y), 48, 22)
    } else {
      const gr = g.createLinearGradient(X(x), 0, X(x) + 14, 0)
      gr.addColorStop(0, 'rgba(60,45,30,.2)')
      gr.addColorStop(1, 'rgba(60,45,30,0)')
      g.fillStyle = gr
      g.fillRect(X(x), Y(y), 14, 40.5)
    }
  }
  g.restore()
}
